import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthenticatedNavigation from '../../components/navigation/AuthenticatedNavigation';
import OnboardingDataDisplay from './components/OnboardingDataDisplay';
import ChatGPTInterface from './components/ChatGPTInterface';
import ProfilePreview from './components/ProfilePreview';
import ProfileEditor from './components/ProfileEditor';
import VersionHistory from './components/VersionHistory';
import AnonymityValidator from './components/AnonymityValidator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { profiles, userSession } from '../../services/localStorageService';

const AnonymousProfileGenerator = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('generator');
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [versions, setVersions] = useState([]);
  const [violations, setViolations] = useState([]);

  const mockCandidateData = {
    experience: "10-15 years",
    language: "Bilingual (EN/FR)",
    location: "Ontario, Canada",
    workMode: "Hybrid",
    seniority: "Senior Manager",
    industry: "Technology",
    companyContext: "Enterprise (1000+ employees)",
    managementScope: "15-30 direct reports",
    businessResponsibility: "P&L ownership",
    budgetManagement: "$5M-$10M annual budget",
    industrySpecific: {
      track: "Software Engineering",
      specializations: ["Cloud Architecture", "Microservices", "DevOps"],
      technologies: ["AWS", "Kubernetes", "Python", "React"],
      certifications: ["AWS Solutions Architect", "Certified Kubernetes Administrator"]
    }
  };

  useEffect(() => {
    // Load existing profile from LocalStorage
    const session = userSession?.get();
    const userId = session?.id || 'demo_user';
    const existingProfile = profiles?.getCurrent(userId);
    
    if (existingProfile) {
      setCurrentProfile(existingProfile);
      const userProfiles = profiles?.getByUserId(userId);
      setVersions(userProfiles);
    } else {
      // Create initial profile if none exists
      const savedProfile = {
        userId,
        content: `Accomplished technology leader with over a decade of experience driving enterprise-scale digital transformation initiatives. Proven track record of building and leading high-performing engineering teams of 15-30 professionals across distributed environments.\n\nDemonstrated expertise in architecting cloud-native solutions and implementing modern DevOps practices that have consistently delivered 40% improvements in deployment velocity and system reliability. Successfully managed multi-million dollar technology budgets while maintaining P&L accountability.\n\nDeep technical proficiency in distributed systems, containerization, and infrastructure automation, combined with strategic business acumen. Experienced in navigating complex organizational dynamics within large enterprises while fostering innovation and technical excellence.\n\nBilingual professional comfortable operating in both English and French-speaking environments, with proven ability to bridge technical and business stakeholders across North American markets.`,
        tone: 'professional',
        emphasis: 'balanced',
        wordCount: 128,
        anonymityScore: 95,
        completenessScore: 88,
        appealScore: 92
      };
      
      const newProfile = profiles?.create(savedProfile);
      setCurrentProfile(newProfile);
      setVersions([newProfile]);
    }
  }, []);

  const handleGenerate = async (options) => {
    setIsGenerating(true);
    await new Promise(resolve => setTimeout(resolve, 2500));

    const session = userSession?.get();
    const userId = session?.id || 'demo_user';
    
    const newProfileData = {
      userId,
      content: generateMockProfile(options),
      tone: options?.tone,
      emphasis: options?.emphasis,
      wordCount: 135,
      anonymityScore: 96,
      completenessScore: 90,
      appealScore: 94
    };
    
    const newProfile = profiles?.create(newProfileData);
    setCurrentProfile(newProfile);
    
    const updatedVersions = profiles?.getByUserId(userId);
    setVersions(updatedVersions);
    setIsGenerating(false);
    setViolations([]);
  };

  const generateMockProfile = (options) => {
    const profiles = {
      professional_technical: `Senior technology executive with extensive experience architecting enterprise-scale cloud infrastructure and leading distributed engineering organizations. Demonstrated success in transforming legacy systems into modern, scalable platforms that support rapid business growth.\n\nProven ability to build and mentor high-performing technical teams while maintaining hands-on involvement in critical architectural decisions. Track record of delivering complex projects on time and within budget constraints, with measurable improvements in system performance and reliability.\n\nExpertise spans cloud-native development, containerization strategies, and DevOps automation, complemented by strong business acumen and P&L management experience. Comfortable navigating technical and strategic discussions with C-level stakeholders.\n\nBilingual professional with deep understanding of North American technology markets and proven ability to drive innovation in enterprise environments.`,
      
      professional_leadership: `Transformational technology leader with proven track record of building and scaling engineering organizations that deliver exceptional business outcomes. Experienced in managing large teams across multiple time zones while fostering cultures of innovation, accountability, and technical excellence.\n\nDemonstrated success in aligning technology strategy with business objectives, resulting in significant revenue growth and operational efficiency gains. Strong background in stakeholder management, with ability to influence and collaborate effectively across all organizational levels.\n\nCombines deep technical knowledge with strategic business thinking and financial acumen. Experienced in managing substantial technology budgets and making data-driven investment decisions that balance innovation with operational stability.\n\nBilingual executive comfortable operating in diverse, multicultural environments with proven ability to drive change in complex enterprise settings.`,
      
      confident_technical: `Accomplished engineering leader who has consistently delivered transformative results in enterprise technology environments. Built reputation for architecting robust, scalable solutions that become foundational to business success.\n\nRecognized for ability to rapidly assess complex technical challenges and implement elegant solutions that exceed performance expectations. Track record of leading teams through ambitious modernization initiatives while maintaining operational excellence.\n\nDeep expertise in cloud infrastructure, distributed systems, and modern development practices, with proven ability to stay ahead of technology trends and make strategic technology choices that provide competitive advantage.\n\nBilingual technology executive with strong presence and communication skills, capable of inspiring teams and influencing senior leadership to embrace bold technical visions.`,
      
      approachable_balanced: `Experienced technology leader passionate about building great products and developing talented teams. Known for creating collaborative environments where engineers thrive and deliver their best work.\n\nBrings over a decade of hands-on technical experience combined with people-focused leadership approach. Believes in empowering teams through clear communication, mentorship, and removing obstacles to success.\n\nStrong technical background in modern cloud technologies and software development practices, with practical experience managing budgets and business outcomes. Comfortable working across technical and business domains to find solutions that work for everyone.\n\nBilingual professional who values diverse perspectives and enjoys working in multicultural teams. Committed to continuous learning and helping others grow in their careers.`
    };

    const key = `${options?.tone}_${options?.emphasis}`;
    return profiles?.[key] || profiles?.professional_technical;
  };

  const handleRegenerate = () => {
    handleGenerate({ tone: currentProfile?.tone, emphasis: currentProfile?.emphasis });
  };

  const handleEdit = () => {
    setView('editor');
  };

  const handleSaveEdit = (content) => {
    const session = userSession?.get();
    const userId = session?.id || 'demo_user';
    
    const updatedProfileData = {
      userId,
      content,
      wordCount: content?.trim()?.split(/\s+/)?.length,
      tone: currentProfile?.tone,
      emphasis: currentProfile?.emphasis,
      anonymityScore: currentProfile?.anonymityScore,
      completenessScore: currentProfile?.completenessScore,
      appealScore: currentProfile?.appealScore
    };

    const updatedProfile = profiles?.create(updatedProfileData);
    setCurrentProfile(updatedProfile);
    
    const updatedVersions = profiles?.getByUserId(userId);
    setVersions(updatedVersions);
    setView('generator');
  };

  const handleCancelEdit = () => {
    setView('generator');
  };

  const handleSelectVersion = (versionId) => {
    const selectedVersion = versions?.find(v => v?.id === versionId);
    if (selectedVersion) {
      profiles?.setAsCurrent(versionId);
      setCurrentProfile(selectedVersion);
    }
  };

  const handleSubmitProfile = () => {
    // Profile is already saved in LocalStorage
    navigate('/opportunity-feed');
  };

  return (
    <div className="min-h-screen bg-[#F6F7FB] pb-20 lg:pb-0 lg:pl-60">
      <AuthenticatedNavigation userRole="candidate" matchCount={3} />
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate('/candidate-onboarding')}
              className="w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center hover:bg-white transition-colors duration-250"
            >
              <Icon name="ArrowLeft" size={20} color="#111827" />
            </button>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-[#111827]">
              Anonymous Profile Generator
            </h1>
          </div>
          <p className="text-sm md:text-base lg:text-lg text-[#6B7280] ml-11 md:ml-13">
            Create your AI-powered professional summary while maintaining complete anonymity
          </p>
        </div>

        {view === 'generator' ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            <div className="space-y-6 md:space-y-8">
              <OnboardingDataDisplay candidateData={mockCandidateData} />
              <ChatGPTInterface onGenerate={handleGenerate} isGenerating={isGenerating} />
              {versions?.length > 1 && (
                <VersionHistory
                  versions={versions}
                  currentVersionId={currentProfile?.id}
                  onSelectVersion={handleSelectVersion}
                />
              )}
            </div>

            <div className="space-y-6 md:space-y-8">
              <ProfilePreview
                profile={currentProfile}
                onRegenerate={handleRegenerate}
                onEdit={handleEdit}
                isGenerating={isGenerating}
              />
              <AnonymityValidator violations={violations} />

              {currentProfile && (
                <div className="bg-white rounded-2xl p-4 md:p-6 border border-[#E5E7EB]">
                  <Button
                    variant="default"
                    size="lg"
                    fullWidth
                    iconName="ArrowRight"
                    iconPosition="right"
                    onClick={handleSubmitProfile}
                  >
                    Submit Profile & Continue
                  </Button>
                  <p className="text-xs md:text-sm text-[#6B7280] text-center mt-3">
                    Your profile will be reviewed by our team before going live
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto">
            <ProfileEditor
              initialContent={currentProfile?.content}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AnonymousProfileGenerator;